import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import CardSlider from 'components/consumer/CardSlider';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import { RectShape, TextRow } from 'react-placeholder/lib/placeholders';
import CardCarousel from 'components/consumer/image-slider-standard/components/CardCarousel';
import useProductListingPageContext from 'data-hooks/useProductListingPageContext';
import useMobileColumnCount from 'components/consumer/ProductListingPage/state/mobileColumnCount/useMobileColumnCount';

const CustomProductListUI = ({ listStyle = 'Slider' }) => {
  const {
    filterStateKey,
    isAllDataLoading,
    loading,
    products,
    productsToPreload,
  } = useProductListingPageContext();

  const mobileColumnCount = useMobileColumnCount();

  const skeletonLoaderItems =
    products?.length > 0 && products?.length < NUMBER_OF_PRODUCTS_TO_SHOW
      ? NUMBER_OF_PRODUCTS_TO_SHOW - products.length
      : NUMBER_OF_PRODUCTS_TO_SHOW;

  const productsList = useMemo(() => {
    const allProductsToShow = [
      ...products,
      ...(productsToPreload ?? []).map(product => ({
        ...product,
        isHidden: true,
      })),
    ];

    return (
      !loading &&
      allProductsToShow?.map(
        (
          {
            campaign,
            id,
            isClearance,
            isHidden,
            label,
            moreVariantsCount,
            name,
            onDisplayInShowroomSlug,
            path,
            position,
            productFamily,
            productLabel,
            productType,
            quickship,
            secondaryImage,
            secondaryImageCropData,
            slug,
            variants,
            variantsToShow,
            variantsTotal,
          },
          index
        ) => (
          <ProductCardStatic
            campaign={campaign}
            enableMobileCarousel={false}
            filterStateKey={filterStateKey}
            hidden={isHidden}
            id={id}
            isClearance={isClearance}
            // The keys for CustomProductList products are derived from the product
            // slug and the index to prevent a duplicate key error when two or
            // more of the same product are included in the custom list.

            // It is safe to use the index because the products in a custom list are returned
            // in the same order with the same indices each time.

            // eslint-disable-next-line react/no-array-index-key
            key={`${slug}-${index}`}
            label={label}
            layout="minimal-with-attributes"
            moreVariantsCount={moreVariantsCount}
            name={name}
            onDisplayInShowroomSlug={onDisplayInShowroomSlug}
            path={path}
            position={position}
            productFamily={productFamily}
            productLabel={productLabel}
            productType={productType}
            quickship={quickship}
            secondaryImage={secondaryImage}
            secondaryImageCropData={secondaryImageCropData}
            slug={slug}
            variants={variants}
            variantsToShow={variantsToShow}
            variantsTotal={variantsTotal}
          />
        )
      )
    );
  }, [loading, products, productsToPreload, filterStateKey]);

  if (listStyle === 'Carousel') {
    return (
      <FixedWidthContainer className="CustomProductListUI">
        <CardCarousel
          visibleSlides={4}
          imageWidth={350}
          imageHeight={350}
          navType="Button"
        >
          {productsList}
        </CardCarousel>
      </FixedWidthContainer>
    );
  }

  if (listStyle === 'Slider') {
    return (
      <div className="max-w-[1500px] mx-auto my-0">
        <CardSlider wide={false}>{productsList}</CardSlider>
      </div>
    );
  }

  if (!loading && !products?.length) {
    return (
      <FixedWidthContainer className="max-[1023px]:!p-0">
        <div className="text-lg font-bold flex flex-col items-center justify-center px-0 py-8 min-h-[30vh]">
          Looks like these deals sold out in a flash! Check back soon for more
          opportunities to save big.
        </div>
      </FixedWidthContainer>
    );
  }

  return (
    <FixedWidthContainer className="max-[1023px]:!p-0">
      <ProductCardGridUI
        limitWidth={false}
        columnsOnDesktop="auto"
        columnsOnMobile={mobileColumnCount}
      >
        {loading &&
          [...Array(skeletonLoaderItems)].map(() => (
            <div className="relative flex flex-col">
              <div className="aspect-square">
                <RectShape
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  className="placeholder-loader"
                />
              </div>
              <TextRow
                className="placeholder-loader"
                color="inherit"
                style={{ width: '50%', height: '20px' }}
              />
              <TextRow
                className="placeholder-loader"
                color="inherit"
                style={{ width: '20%', height: '15px' }}
              />
            </div>
          ))}
        {productsList}
        {!loading &&
          isAllDataLoading &&
          [...Array(skeletonLoaderItems)].map(() => (
            <div className="relative flex flex-col">
              <div className="aspect-square">
                <RectShape
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  className="placeholder-loader"
                />
              </div>
              <TextRow
                className="placeholder-loader"
                color="inherit"
                style={{ width: '50%', height: '20px' }}
              />
              <TextRow
                className="placeholder-loader"
                color="inherit"
                style={{ width: '20%', height: '15px' }}
              />
            </div>
          ))}
      </ProductCardGridUI>
    </FixedWidthContainer>
  );
};

CustomProductListUI.propTypes = {
  listStyle: PropTypes.oneOf(['Slider', 'Carousel', 'Grid']),
};

export default CustomProductListUI;
